// Smart Sash Admin Panel - User Service
// CRUD operations for user management (v2 API)

import {
  ApiPaginatedData,
  ChangePasswordRequest,
  CreateUserRequest,
  CreateUserResponse,
  ForgotPasswordRequest,
  PaginatedResponse,
  PaginationParams,
  ResetPasswordRequest,
  UpdateUserRequest,
  UserCredentials,
  UserDTO,
  UserFilterOptions,
  toPaginatedResponse,
} from '@/models';
import { axiosClient } from '../http/axiosClient';

const BASE_PATH = '/User';

/**
 * User Service (v2)
 */
export const userService = {
  /**
   * Get all users with pagination and filters
   */
  getAll: async (
    params?: PaginationParams,
    filters?: UserFilterOptions
  ): Promise<PaginatedResponse<UserDTO>> => {
    const response = await axiosClient.get<ApiPaginatedData<UserDTO> | UserDTO[]>(BASE_PATH, {
      params: { ...params, ...filters },
    });
    return toPaginatedResponse(response.data);
  },

  /**
   * Get user by composite key (institutionId + userId)
   */
  getById: async (institutionId: string, userId: string): Promise<UserDTO> => {
    const response = await axiosClient.get<UserDTO>(`${BASE_PATH}/${institutionId}/${userId}`);
    return response.data;
  },

  /**
   * Create a regular user (backend generates credentials)
   */
  createUser: async (data: CreateUserRequest): Promise<CreateUserResponse> => {
    const response = await axiosClient.post<CreateUserResponse>(`${BASE_PATH}/user`, data);
    return response.data;
  },

  /**
   * Create an admin user (backend generates credentials)
   */
  createAdmin: async (data: CreateUserRequest): Promise<CreateUserResponse> => {
    const response = await axiosClient.post<CreateUserResponse>(`${BASE_PATH}/admin`, data);
    return response.data;
  },

  /**
   * Update existing user by composite key
   */
  update: async (
    institutionId: string,
    userId: string,
    data: UpdateUserRequest
  ): Promise<UserDTO> => {
    const response = await axiosClient.put<UserDTO>(
      `${BASE_PATH}/${institutionId}/${userId}`,
      data
    );
    return response.data;
  },

  /**
   * Delete user by composite key
   */
  delete: async (institutionId: string, userId: string): Promise<void> => {
    await axiosClient.delete(`${BASE_PATH}/${institutionId}/${userId}`);
  },

  /**
   * Change password (moved from authService in v2)
   */
  changePassword: async (request: ChangePasswordRequest): Promise<void> => {
    await axiosClient.put(`${BASE_PATH}/change-password`, request);
  },

  /**
   * Regenerate a temporary password for the given user (admin action)
   */
  regenerateTempPassword: async (
    institutionId: string,
    userId: string
  ): Promise<UserCredentials> => {
    const response = await axiosClient.post<UserCredentials>(
      `${BASE_PATH}/${institutionId}/${userId}/regenerate-temp-password`
    );
    return response.data;
  },

  /**
   * Request password reset email
   */
  forgotPassword: async (request: ForgotPasswordRequest): Promise<void> => {
    await axiosClient.post(`${BASE_PATH}/forgot-password`, request);
  },

  /**
   * Reset password with token
   */
  resetPassword: async (request: ResetPasswordRequest): Promise<void> => {
    await axiosClient.post(`${BASE_PATH}/reset-password`, request);
  },
};

export default userService;
